import { forwardRef, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, ShoppingBag, Search, User, ClipboardList } from 'lucide-react';
import { useCartStore } from '@/store/cartStore';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

const BottomNav = memo(forwardRef<HTMLElement>((_, ref) => {
  const location = useLocation();
  const totalItems = useCartStore((state) => state.getTotalItems());
  const { user, isAdmin, isEmployee } = useAuth();
  
  const isStaff = isAdmin || isEmployee;

  const navItems = [
    { path: '/', icon: Home, label: 'الرئيسية' },
    isStaff 
      ? { path: '/admin/orders', icon: ClipboardList, label: 'الطلبات' }
      : { path: '/search', icon: Search, label: 'البحث' },
    { path: '/cart', icon: ShoppingBag, label: 'السلة', badge: totalItems > 0 ? totalItems : undefined },
    { path: user ? '/admin' : '/auth', icon: User, label: user ? 'الإدارة' : 'تسجيل' },
  ];

  return (
    <nav ref={ref} className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background shadow-[0_-12px_35px_-24px_hsl(24_24%_12%/0.45)] safe-area-bottom md:hidden">
      <div className="mx-auto flex h-[68px] max-w-lg items-center justify-around px-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'relative flex h-full flex-1 flex-col items-center justify-center gap-1 transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <div className="relative">
                <Icon className={cn('h-5 w-5 transition-transform', isActive && 'scale-110')} strokeWidth={isActive ? 2.5 : 2} />
                {item.badge && item.badge > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {item.badge}
                  </span>
                )}
              </div>
              <span className={cn('text-[10px] font-medium', isActive && 'font-black')}>{item.label}</span>
              {isActive && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-primary" />}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}));

BottomNav.displayName = 'BottomNav';

export default BottomNav;
